import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import API from '../config/api';
import { downloadInvoicePdf } from '../utils/invoicePdf';

function parseAddons(order) {
  if (!order?.addons_json) return [];
  try {
    const parsed = JSON.parse(order.addons_json);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

export default function OrderDetail() {
  const { orderPublicId } = useParams();
  const { token } = useContext(AuthContext);
  const [orderBundle, setOrderBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!orderPublicId) {
        setError('Order reference is missing.');
        setLoading(false);
        return;
      }

      try {
        const localCtxRaw = sessionStorage.getItem('kh_last_order_context');
        const localCtx = localCtxRaw ? JSON.parse(localCtxRaw) : null;
        const fallbackEmail = localCtx?.orderPublicId === orderPublicId ? (localCtx.email || '') : '';
        const fallbackPhone = localCtx?.orderPublicId === orderPublicId ? (localCtx.phone || '') : '';

        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await fetch(API.orders.get(orderPublicId, fallbackEmail, fallbackPhone), { headers });
        const data = await res.json();

        if (!res.ok || data.status !== 'success') {
          throw new Error(data.message || 'Unable to load order details.');
        }

        setOrderBundle(data.data);
      } catch (err) {
        setError(err.message || 'Unable to load order details.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [orderPublicId, token]);

  const order = orderBundle?.order || null;
  const items = orderBundle?.items || [];
  const payment = orderBundle?.payment || null;
  const shipping = orderBundle?.shipping || null;
  const addons = useMemo(() => parseAddons(order), [order]);

  const status = (order?.status || 'pending').toLowerCase();

  return (
    <main className="kh-order-detail-page" style={{ minHeight: '100vh', background: '#f5f3ee', padding: '56px 20px' }}>
      <section style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {loading && <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #ece6d9', padding: '20px' }}>Loading order...</div>}

        {!loading && error && (
          <div style={{ background: '#fff2f2', borderRadius: '12px', border: '1px solid #f5c2c2', padding: '20px' }}>
            <p style={{ margin: '0 0 12px', color: '#9c1d1d' }}>{error}</p>
            <Link to="/orders" className="btn-primary">Back To Orders</Link>
          </div>
        )}

        {!loading && !error && order && (
          <>
            <div style={{ background: '#fff', border: '1px solid #ece6d9', borderRadius: '12px', padding: '22px', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
              <div>
                <p style={{ margin: 0, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: '#8a8478' }}>Order Reference</p>
                <h1 style={{ margin: '8px 0 0', fontFamily: 'Noto Serif, Georgia, serif', fontWeight: 500, color: '#1f1d16' }}>{order.order_public_id || `#${order.id}`}</h1>
                <p style={{ margin: '8px 0 0', color: '#5d584d' }}>Placed on {new Date(order.created_at).toLocaleString()}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: 0, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: '#8a8478' }}>Current Status</p>
                <p style={{ margin: '8px 0 0', fontSize: '16px', color: status === 'paid' || status === 'shipped' || status === 'delivered' ? '#0e7d3f' : '#9b6a00', textTransform: 'uppercase', fontWeight: 700 }}>{status}</p>
              </div>
            </div>

            <div className="kh-order-detail-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px' }}>
              <div style={{ background: '#fff', border: '1px solid #ece6d9', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ marginTop: 0, color: '#1f1d16' }}>Items</h3>
                <div style={{ display: 'grid', gap: '10px' }}>
                  {items.map((item, index) => (
                    <div key={index} className="kh-order-detail-item-row" style={{ borderBottom: '1px solid #f0ebdf', paddingBottom: '8px', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                      <div>
                        <p style={{ margin: 0, color: '#1f1d16', fontWeight: 600 }}>{item.product_name || 'Product'}</p>
                        <p style={{ margin: '4px 0 0', color: '#6b665b', fontSize: '13px' }}>Size: {item.selected_size || '-'} | Qty: {item.quantity}</p>
                      </div>
                      <p style={{ margin: 0, color: '#1f1d16', fontWeight: 700 }}>₹{(Number(item.locked_price || 0) * Number(item.quantity || 0)).toLocaleString('en-IN')}</p>
                    </div>
                  ))}
                </div>
              </div>

              <aside style={{ background: '#fff', border: '1px solid #ece6d9', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ marginTop: 0, color: '#1f1d16' }}>Summary</h3>
                <div style={{ display: 'grid', gap: '8px', fontSize: '14px', color: '#524d43' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><strong>₹{Number(order.subtotal_amount || 0).toLocaleString('en-IN')}</strong></div>
                  {addons.map((addon, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}><span>{addon.label || 'Addon'}</span><strong>₹{Number(addon.amount || 0).toLocaleString('en-IN')}</strong></div>
                  ))}
                  {!addons.length && Number(order.addons_total || 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Add-ons</span><strong>₹{Number(order.addons_total || 0).toLocaleString('en-IN')}</strong></div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Shipping</span><strong>{Number(order.shipping_amount || 0) > 0 ? `₹${Number(order.shipping_amount || 0).toLocaleString('en-IN')}` : 'FREE'}</strong></div>
                  <div style={{ borderTop: '1px solid #eee4d3', marginTop: '4px', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontSize: '16px', color: '#1f1d16' }}><span>Total</span><strong>₹{Number(order.total_amount || 0).toLocaleString('en-IN')}</strong></div>
                </div>

                <button className="kh-order-detail-action" onClick={() => downloadInvoicePdf(orderBundle)} style={{ width: '100%', marginTop: '14px', padding: '10px 14px', borderRadius: '8px', border: '1px solid #d4c7a9', background: '#fff', color: '#7d5e12', fontSize: '12px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>Download PDF Bill</button>
                <Link className="kh-order-detail-action" to="/orders" style={{ width: '100%', marginTop: '10px', display: 'inline-block', textAlign: 'center', textDecoration: 'none', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', color: '#3f3f3f', fontSize: '12px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>Back To Orders</Link>
              </aside>
            </div>

            <div style={{ marginTop: '14px', background: '#fff', border: '1px solid #ece6d9', borderRadius: '12px', padding: '18px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#1f1d16' }}>Shipping & Payment</h3>
              <p style={{ margin: '0 0 6px', color: '#595447' }}>{order.guest_name || '-'}</p>
              <p style={{ margin: '0 0 6px', color: '#595447' }}>{order.guest_email || '-'} | {order.guest_phone || '-'}</p>
              {shipping && (
                <p style={{ margin: '0 0 6px', color: '#595447' }}>
                  {[shipping.line1, shipping.line2, shipping.city, shipping.state, shipping.postal_code, shipping.country].filter(Boolean).join(', ')}
                </p>
              )}
              <p style={{ margin: 0, color: '#595447' }}>Payment Provider: {payment?.provider || order.payment_provider || 'razorpay'} {payment?.provider_payment_id ? `| Payment ID: ${payment.provider_payment_id}` : ''}</p>
            </div>
          </>
        )}
      </section>
      <style>{`
        @media (max-width: 820px) {
          .kh-order-detail-page { padding: 28px 12px !important; }
          .kh-order-detail-grid { grid-template-columns: 1fr !important; }
          .kh-order-detail-item-row { flex-direction: column; gap: 6px; }
        }
        @media (max-width: 620px) {
          .kh-order-detail-action { width: 100% !important; }
        }
      `}</style>
    </main>
  );
}
